// constants/i18n.js
// UI internationalization — translations for all user-facing strings.
// Add new languages by adding a key to TRANSLATIONS and UI_LANGUAGES.

export const UI_LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'sv', label: 'Svenska', flag: '🇸🇪' },
];

const en = {
  // Navigation
  'nav.dashboard': 'Dashboard',
  'nav.editor': 'Editor',
  'nav.qa': 'QA',
  'nav.clips': 'Clips',
  'nav.admin': 'Admin',
  'nav.export': 'Export',
  'nav.tools': 'Tools',

  // Editor tabs
  'editor.unified': 'Unified',
  'editor.history': 'History',
  'editor.langFiles': 'Lang Files',
  'editor.glossary': 'Glossary',

  // Editor controls
  'editor.markError': 'Mark Error',
  'editor.markQuote': 'Mark Quote',
  'editor.interviewLanguage': 'Interview Language',
  'editor.searchMarks': 'Search marks...',
  'editor.height': 'Height',
  'editor.zoom': 'Zoom',

  // Filter pills
  'filter.all': 'All',
  'filter.edited': 'Edited',
  'filter.pending': 'Pending',
  'filter.flagged': 'Flagged',
  'filter.qa': 'QA',
  'filter.assigned': 'Assigned',
  'filter.reviewed': 'Reviewed',

  // Clips
  'clips.clipReview': 'Clip Review',
  'clips.exportMp4': 'Export MP4',
  'clips.exportSvg': 'Export SVG',
  'clips.exportPng': 'PNG',
  'clips.overlay': 'Overlay',
  'clips.approve': 'Approve',
  'clips.unapprove': 'Unapprove',
  'clips.reject': 'Reject',
  'clips.flag': 'Flag',
  'clips.essentialClips': 'Essential Clips',
  'clips.introClips': 'Intro Clips',
  'clips.voiceClips': 'Voice Clips',
  'clips.memorableQuotes': 'Memorable Quotes',
  'clips.assembly': 'Assembly',

  // QR Designer tabs
  'qr.qrCode': 'QR Code',
  'qr.gravemarker': 'Gravemarker',
  'qr.memorialPage': 'Memorial Page',
  'qr.mediaLibrary': 'Media Library',

  // Admin
  'admin.dashboard': 'Admin Dashboard',
  'admin.users': 'Users',
  'admin.statistics': 'Statistics',
  'admin.ai': 'AI',
  'admin.apiKeys': 'API Keys & DB',
  'admin.helpMenus': 'Help Menus',
  'admin.tools': 'Tools',
  'admin.workspace': 'Workspace',

  // Common actions
  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.delete': 'Delete',
  'action.edit': 'Edit',
  'action.add': 'Add',
  'action.close': 'Close',
  'action.refresh': 'Refresh',
  'action.search': 'Search',
  'action.apply': 'Apply',
  'action.undo': 'Undo',
  'action.download': 'Download',
  'action.upload': 'Upload',
  'action.copy': 'Copy',
  'action.preview': 'Preview',
  'action.generate': 'Generate',
  'action.export': 'Export',

  // Export menu
  'export.writeVtts': 'Write Corrected VTTs to Disk',
  'export.refreshAll': 'Refresh All VTT Files',
  'export.viewReport': 'View Report',
  'export.downloadReport': 'Download Report',
  'export.notebookLm': 'Export for NotebookLM',

  // Pipeline stages
  'stage.transcribe': 'Transcribe',
  'stage.translate': 'Translate',
  'stage.reviewMark': 'Review & Mark',
  'stage.reproof': 'Re-proof',
  'stage.generateOutput': 'Generate Output',
  'stage.qaReview': 'QA Review',
  'stage.clipsQr': 'Clips & QR',
  'stage.finalReview': 'Final Review',
  'stage.complete': 'Complete',

  // Status
  'status.pending': 'Pending',
  'status.inProgress': 'In Progress',
  'status.done': 'Done',
  'status.skipped': 'Skipped',

  // Dashboard
  'dashboard.board': 'Board',
  'dashboard.sessionHours': 'Session Hours',
  'dashboard.trackedWork': 'Tracked Work',
  'dashboard.aiSessions': 'AI Sessions',
  'dashboard.aiCost': 'AI Cost',
  'dashboard.locations': 'Locations',
  'dashboard.pendingRequests': 'Pending Requests',

  // Glossary
  'glossary.title': 'Glossary',
  'glossary.search': 'Search glossary...',
  'glossary.addRule': 'Add Rule',
  'glossary.applyAll': 'Apply All',
  'glossary.undoAuto': 'Undo Auto',
  'glossary.thisProject': 'This project',
  'glossary.global': 'Global',
  'glossary.errorMisspelling': 'Error / misspelling',
  'glossary.correctionFix': 'Correction / fix',
  'glossary.projectRules': 'Project rules',
  'glossary.globalRules': 'Global rules',
  'glossary.noRules': 'No glossary rules found. Add one above.',

  // Tape cards
  'tape.tape': 'Tape',
  'tape.pending': 'Pending',
  'tape.done': 'Done',

  // Misc
  'misc.noData': 'No data available',
  'misc.loading': 'Loading...',
  'misc.selectLanguage': 'Select language...',
  'misc.devEnvironment': 'Dev Environment',
};

const sv = {
  // Navigation
  'nav.dashboard': 'Översikt',
  'nav.editor': 'Redigerare',
  'nav.qa': 'QA',
  'nav.clips': 'Klipp',
  'nav.admin': 'Admin',
  'nav.export': 'Exportera',
  'nav.tools': 'Verktyg',

  // Editor tabs
  'editor.unified': 'Samlad',
  'editor.history': 'Historik',
  'editor.langFiles': 'Språkfiler',
  'editor.glossary': 'Ordlista',

  // Editor controls
  'editor.markError': 'Markera Fel',
  'editor.markQuote': 'Markera Citat',
  'editor.interviewLanguage': 'Intervjuspråk',
  'editor.searchMarks': 'Sök markeringar...',
  'editor.height': 'Höjd',
  'editor.zoom': 'Zoom',

  // Filter pills
  'filter.all': 'Alla',
  'filter.edited': 'Redigerade',
  'filter.pending': 'Väntande',
  'filter.flagged': 'Flaggade',
  'filter.qa': 'QA',
  'filter.assigned': 'Tilldelade',
  'filter.reviewed': 'Granskade',

  // Clips
  'clips.clipReview': 'Klippgranskning',
  'clips.exportMp4': 'Exportera MP4',
  'clips.exportSvg': 'Exportera SVG',
  'clips.exportPng': 'PNG',
  'clips.overlay': 'Textlager',
  'clips.approve': 'Godkänn',
  'clips.unapprove': 'Ångra godkännande',
  'clips.reject': 'Avvisa',
  'clips.flag': 'Flagga',
  'clips.essentialClips': 'Viktiga klipp',
  'clips.introClips': 'Introklipp',
  'clips.voiceClips': 'Röstklipp',
  'clips.memorableQuotes': 'Minnesvärda citat',
  'clips.assembly': 'Sammanställning',

  // QR Designer tabs
  'qr.qrCode': 'QR-kod',
  'qr.gravemarker': 'Gravmarkör',
  'qr.memorialPage': 'Minnessida',
  'qr.mediaLibrary': 'Mediabibliotek',

  // Admin
  'admin.dashboard': 'Adminpanel',
  'admin.users': 'Användare',
  'admin.statistics': 'Statistik',
  'admin.ai': 'AI',
  'admin.apiKeys': 'API-nycklar & DB',
  'admin.helpMenus': 'Hjälpmenyer',
  'admin.tools': 'Verktyg',
  'admin.workspace': 'Arbetsyta',

  // Common actions
  'action.save': 'Spara',
  'action.cancel': 'Avbryt',
  'action.delete': 'Radera',
  'action.edit': 'Redigera',
  'action.add': 'Lägg till',
  'action.close': 'Stäng',
  'action.refresh': 'Uppdatera',
  'action.search': 'Sök',
  'action.apply': 'Tillämpa',
  'action.undo': 'Ångra',
  'action.download': 'Ladda ner',
  'action.upload': 'Ladda upp',
  'action.copy': 'Kopiera',
  'action.preview': 'Förhandsvisa',
  'action.generate': 'Generera',
  'action.export': 'Exportera',

  // Export menu
  'export.writeVtts': 'Skriv korrigerade VTT-filer till disk',
  'export.refreshAll': 'Uppdatera alla VTT-filer',
  'export.viewReport': 'Visa rapport',
  'export.downloadReport': 'Ladda ner rapport',
  'export.notebookLm': 'Exportera för NotebookLM',

  // Pipeline stages
  'stage.transcribe': 'Transkribera',
  'stage.translate': 'Översätt',
  'stage.reviewMark': 'Granska & Markera',
  'stage.reproof': 'Korrekturläs',
  'stage.generateOutput': 'Generera utdata',
  'stage.qaReview': 'QA-granskning',
  'stage.clipsQr': 'Klipp & QR',
  'stage.finalReview': 'Slutgranskning',
  'stage.complete': 'Klar',

  // Status
  'status.pending': 'Väntande',
  'status.inProgress': 'Pågår',
  'status.done': 'Klar',
  'status.skipped': 'Överhoppad',

  // Dashboard
  'dashboard.board': 'Tavla',
  'dashboard.sessionHours': 'Sessionstimmar',
  'dashboard.trackedWork': 'Spårat arbete',
  'dashboard.aiSessions': 'AI-sessioner',
  'dashboard.aiCost': 'AI-kostnad',
  'dashboard.locations': 'Platser',
  'dashboard.pendingRequests': 'Väntande förfrågningar',

  // Glossary
  'glossary.title': 'Ordlista',
  'glossary.search': 'Sök i ordlistan...',
  'glossary.addRule': 'Lägg till regel',
  'glossary.applyAll': 'Tillämpa alla',
  'glossary.undoAuto': 'Ångra auto',
  'glossary.thisProject': 'Detta projekt',
  'glossary.global': 'Globalt',
  'glossary.errorMisspelling': 'Fel / felstavning',
  'glossary.correctionFix': 'Korrigering / fix',
  'glossary.projectRules': 'Projektregler',
  'glossary.globalRules': 'Globala regler',
  'glossary.noRules': 'Inga ordlisteregler hittades. Lägg till en ovan.',

  // Tape cards
  'tape.tape': 'Band',
  'tape.pending': 'Väntande',
  'tape.done': 'Klar',

  // Misc
  'misc.noData': 'Ingen data tillgänglig',
  'misc.loading': 'Laddar...',
  'misc.selectLanguage': 'Välj språk...',
  'misc.devEnvironment': 'Utvecklingsmiljö',
};

export const TRANSLATIONS = { en, sv };

/**
 * Get a translation for the given key and locale.
 * Falls back to English if the key is missing in the target locale.
 */
export function t(key, locale = 'en') {
  return TRANSLATIONS[locale]?.[key] || TRANSLATIONS.en?.[key] || key;
}
